import type { Theme, ThemeOptions } from '@mui/material/styles';
import { ButtonProps as MuiButtonProps } from '@mui/material/Button';

const startEndIconSizing = {
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    margin: 0,
    '& .MuiSvgIcon-root': {
      fontSize: 16,
      width: 16,
      height: 16
    }
  }
} as const;

const sizeSmallSizing = {
  height: 'auto',
  minHeight: 30,
  padding: '4px 10px',
  fontSize: '0.8125rem',
  lineHeight: 1.2
} as const;

const sizeMediumSizing = {
  height: 37,
  minHeight: 40,
  padding: '6px 14px'
} as const;

const sharedRoot = (size?: MuiButtonProps['size']) =>
  ({
    ...startEndIconSizing,
    ...(size === 'small' ? sizeSmallSizing : sizeMediumSizing),
    ...(size === 'small' && { '&.MuiButton-sizeSmall': sizeSmallSizing }),
    gap: 5,
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 500
  } as const);

function variantRootStyles(theme: Theme, ownerState: { variant?: string; color?: string }) {
  if (ownerState.variant === 'contained') {
    const color = ownerState.color ?? 'primary';
    if (color === 'primary') {
      return {
        fontWeight: 700,
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
          boxShadow: 'none'
        }
      };
    }
    return {
      fontWeight: 700,
      '&:hover': {
        boxShadow: 'none'
      }
    };
  }
  if (ownerState.variant === 'outlined') {
    const color = ownerState.color ?? 'primary';
    if (color === 'primary') {
      return {
        border: `1px solid ${theme.palette.primary.main}`,
        color: theme.palette.primary.main,
        '&:hover': {
          borderColor: theme.palette.primary.dark,
          color: theme.palette.primary.dark,
          backgroundColor: theme.palette.action.hover,
          boxShadow: 'none'
        }
      };
    }
    return {
      '&:hover': {
        boxShadow: 'none'
      }
    };
  }
  if (ownerState.variant === 'text') {
    const color = ownerState.color ?? 'primary';
    if (color === 'primary') {
      return {
        color: theme.palette.primary.main,
        '& .MuiTouchRipple-root': {
          display: 'none'
        },
        '&:hover': {
          color: theme.palette.primary.main,
          backgroundColor: 'transparent',
          textDecoration: 'underline'
        },
        '&:active': {
          color: theme.palette.primary.dark,
          backgroundColor: 'transparent',
          textDecoration: 'none'
        }
      };
    }
    return {
      '& .MuiTouchRipple-root': {
        display: 'none'
      },
      '&:hover': {
        backgroundColor: 'transparent',
        textDecoration: 'underline'
      },
      '&:active': {
        backgroundColor: 'transparent',
        textDecoration: 'none'
      }
    };
  }
  return {};
}

export function buttonThemeComponents(theme: Theme): ThemeOptions['components'] {
  return {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...sharedRoot(ownerState.size),
          '&.SmButton-shape-square': {
            borderRadius: 10
          },
          ...variantRootStyles(theme, ownerState)
        }),
        outlined: {
          fontWeight: 600
        },
        sizeSmall: sizeSmallSizing
      }
    }
  };
}
